/**
 * Pick the workspace id the embedded shell should bind to.
 *
 * v0.1.47: sticky last workspace. The embedded Claude is workspace-
 * stateful (auth, region, working context), so its world should NOT
 * reset every time the user goes Home in the desktop UI. Home is a UX
 * scope (inbox, DMs) — it isn't a workspace Claude can live in.
 *
 * Why this matters: Zero rebinds region on workspace change, but
 * Claude doesn't speak Zero — it speaks credentials.json + HTTP. So
 * the two layers can decouple: Zero follows the UI scope, Claude
 * follows the user's last picked workspace.
 *
 * Rule (v0.1.47+):
 *
 *   1. If the user is currently in a workspace (`selectedWorkspace`
 *      set), use it.
 *   2. Else if we remember the LAST workspace the user explicitly
 *      picked this session (`lastSelectedWorkspace`), keep using it.
 *      This is the load-bearing change: clicking Home no longer
 *      disturbs the shell.
 *   3. Else cold-start: if the user has a workspace in their
 *      `home_region`, prefer that one — when nothing else is known,
 *      the user belongs to their home region.
 *   4. Else fall through to `workspaceList[0]`. Users with only
 *      foreign-region workspaces still get a working shell.
 *   5. Else null. No workspaces at all → no shell (correctly gated by
 *      AppShell, which won't mount TerminalPanel when this is null).
 *
 * Pre-v0.1.47 (v0.1.46) had no step 2 — going Home re-fired the
 * cold-start logic on every click and could pick a different home-
 * region workspace than the one the user just left. That was a
 * regression on its own; the sticky-last-workspace rule fixes it.
 */
export function pickTerminalWorkspaceId(args: {
  selectedWorkspace: string | null;
  /**
   * The last non-null `selectedWorkspace` for this session, tracked
   * by `use-workspace-scope.ts` in a ref that updates only when the
   * user picks a real workspace (never on Home). Survives multiple
   * Home<->workspace transitions but resets across page reloads.
   */
  lastSelectedWorkspace: string | null;
  workspaceList: ReadonlyArray<{ id: string; region?: string | null }>;
  userHomeRegion: "us" | "eu" | null;
}): string | null {
  const {
    selectedWorkspace,
    lastSelectedWorkspace,
    workspaceList,
    userHomeRegion,
  } = args;
  if (selectedWorkspace) return selectedWorkspace;
  // Sticky: only honor a remembered id if it's still in the user's
  // workspace list (it could have been removed since they picked it —
  // e.g. they got kicked, or it was archived).
  if (
    lastSelectedWorkspace &&
    workspaceList.some((w) => w.id === lastSelectedWorkspace)
  ) {
    return lastSelectedWorkspace;
  }
  if (userHomeRegion) {
    const inHome = workspaceList.find((w) => w.region === userHomeRegion);
    if (inHome) return inHome.id;
  }
  return workspaceList[0]?.id ?? null;
}
